<script setup lang="ts">
// #724: 魔法研究会のお気に入りトグル（★）。いいね（♥）とは別概念:
// - いいね = 公開の称賛（匿名可）／お気に入り = 自分用の非公開ブックマーク（サインイン必須）。
// お気に入り操作のロジックを 1 箇所に集約し、WorkCard（デスクトップグリッド）・
// arcanelab モバイルグリッド・公開プレイ面（PlayImmersiveHeader / PlayDialog）で再利用する。
import { mdiStar, mdiStarOutline } from '@mdi/js'
import type { Work } from '~/composables/api/useWorkApi'

interface Props {
  work: Work
}

const props = defineProps<Props>()

const { favoriteWork, unfavoriteWork } = useApi()
const { isAuthenticated } = useAuthInfo()
const { open: openSignInCta } = useSignInCta()

// #752 P1-1: お気に入り状態は露出面（ギャラリーグリッド / 没入ヘッダー / モーダル /
// ゆりかご結晶タブ）を跨いで共有する。コンポーネントローカルだと、同じ作品の別
// インスタンスが後からマウントした際に古い `work.favorited` を読み、ゆりかご結晶タブの
// windowed→ワイド切替（モーダル★→没入ヘッダー★の再マウント）で★が消えて見えた。
// Nuxt useState で workId→favorited を 1 枚だけ持ち、全インスタンスがそこを参照/更新する
// （SSR-safe・リアクティブ）。非公開機能は「アイコン状態が唯一のフィードバック」なので
// 面ごとにブレると信頼を直撃する（Fable レビュー #752）。
const favorites = useState<Record<string, boolean>>('work-favorites', () => ({}))
const workId = computed(() => props.work.workId)

// 初回のみ prop の favorited で種を張る。既にストアにあれば操作結果（＝より新しい）が
// 正なので上書きしない。
const seedFavorite = (w: Work) => {
  if (favorites.value[w.workId] === undefined) {
    favorites.value[w.workId] = w.favorited ?? false
  }
}
seedFavorite(props.work)
watch(() => props.work, seedFavorite)

const isFavorited = computed(() => favorites.value[workId.value] ?? props.work.favorited ?? false)
const loading = ref(false)

// slug は Work.slug 優先、無ければ publicUrl ("/works/{slug}") 末尾から。
const workSlug = computed(() => props.work.slug || props.work.publicUrl.split('/').pop() || '')

// #752: いいね♥ と挙動を統一する。★は露出面に依らず常時クリック可で、
// 未ログイン click は（トグルせず）サインイン CTA を開く（#751 の♥導線と同じ）。
// ツールチップも♥同様「操作名」だけにし、サインイン誘導の長文は出さない。
const tooltip = computed(() =>
  isFavorited.value ? 'お気に入りから外す' : 'お気に入りに追加',
)

const onClick = async () => {
  if (loading.value) return
  // 未ログインはトグルせずサインイン CTA を開く（いいねと同じ導線）。
  if (!isAuthenticated) {
    openSignInCta('favorite')
    return
  }

  const id = workId.value
  const was = isFavorited.value
  // Optimistic update（共有ストア経由なので全露出面のインスタンスに即反映される）。
  favorites.value[id] = !was
  loading.value = true

  try {
    const res = was
      ? await unfavoriteWork(workSlug.value)
      : await favoriteWork(workSlug.value)
    favorites.value[id] = res.favorited
  }
  catch {
    // Revert on error
    favorites.value[id] = was
  }
  finally {
    loading.value = false
  }
}
</script>

<template>
  <v-tooltip
    eager
    :text="tooltip"
    :aria-label="tooltip"
    location="top"
  >
    <template #activator="{ props: tooltipProps }">
      <span
        v-bind="tooltipProps"
        class="favorite-btn"
        :class="{ 'is-favorited': isFavorited }"
        role="button"
        :aria-label="tooltip"
        :aria-pressed="isAuthenticated ? isFavorited : undefined"
        @click.stop="onClick"
      >
        <v-icon
          :icon="isFavorited ? mdiStar : mdiStarOutline"
          :color="isFavorited ? '#ffca28' : ''"
        />
      </span>
    </template>
  </v-tooltip>
</template>

<style scoped>
.favorite-btn {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  opacity: 0.7;
  transition: opacity 0.15s, transform 0.1s;
}

.favorite-btn:hover {
  opacity: 1;
}

.favorite-btn.is-favorited {
  opacity: 1;
}

.favorite-btn:active {
  transform: scale(1.25);
}
</style>
